import React from 'react';

export interface Column<T> {
  key: string;
  label: string;
  render?: (item: T) => React.ReactNode;
}

interface DataTableProps<T> {
  title: string;
  columns: Column<T>[];
  data: T[];
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRefresh: () => void;
  onAdd?: () => void;
  onSearch?: (query: string) => void;
  onFilter?: () => void;
  filterActive?: boolean;
  actions?: (item: T) => React.ReactNode;
  rowMenu?: (item: T) => React.ReactNode;
  headerControls?: React.ReactNode;
  alignTop?: boolean;
  rowClassName?: (item: T) => string;
}

export function DataTable<T extends { id: string }>({
  title,
  columns,
  data,
  page,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
  onRefresh,
  onAdd,
  onSearch,
  onFilter,
  filterActive,
  actions,
  rowMenu,
  headerControls,
  alignTop,
  rowClassName,
}: DataTableProps<T>) {
  const hasNext = data.length === pageSize;
  const hasPrev = page > 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        <div className="flex items-center gap-2">
          {headerControls}
          {onSearch && (
            <input
              type="text"
              placeholder="Search..."
              onChange={(e) => onSearch(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
            />
          )}
          {onFilter && (
            <button
              onClick={onFilter}
              className={`p-2 rounded-lg transition-colors ${filterActive ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-50' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
              title="Filter"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={filterActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </button>
          )}
          {onAdd && (
            <button
              onClick={onAdd}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              title="Add"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          )}
          <button
            onClick={onRefresh}
            className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 2v6h-6"/>
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8"/>
              <path d="M3 22v-6h6"/>
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto min-h-[300px]">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100">
              {columns.map((col) => (
                <th key={col.key} className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">
                  {col.label}
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3" />
              )}
              {rowMenu && (
                <th className="px-4 py-3" />
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0) + (rowMenu ? 1 : 0)} className="px-4 py-12 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0) + (rowMenu ? 1 : 0)} className="px-4 py-12 text-center text-gray-400">
                  No results found
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr key={item.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors${rowClassName ? ' ' + rowClassName(item) : ''}`}>
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-3 text-sm text-gray-700${alignTop ? ' align-top' : ''}`}>
                      {col.render ? col.render(item) : (item as Record<string, unknown>)[col.key] as React.ReactNode}
                    </td>
                  ))}
                  {actions && (
                    <td className={`px-4 py-3 text-right${alignTop ? ' align-top' : ''}`}>
                      <div className="flex items-center justify-end gap-2">
                        {actions(item)}
                      </div>
                    </td>
                  )}
                  {rowMenu && (
                    <td className={`px-4 py-3 text-right${alignTop ? ' align-top' : ''}`}>
                      {rowMenu(item)}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer pagination */}
      <div className="flex items-center justify-end gap-4 px-4 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded px-2 py-1"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={!hasPrev}
            className={`text-sm px-3 py-1 rounded ${hasPrev ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
          >
            &larr; Previous
          </button>
          <span className="text-sm text-gray-500">Page {page}</span>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={!hasNext}
            className={`text-sm px-3 py-1 rounded ${hasNext ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'}`}
          >
            Next &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}
